import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CircleDot, Zap, ScanFace, Sparkles, Eye, Leaf, Users } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import AnimatedSection from '../components/AnimatedSection';
import { useLanguage } from '../i18n/pageTranslations';

const MedicinaEstetica = () => {
  const lang = useLanguage();

  const texts = {
    es: {
      eyebrow: 'Medicina Estética y Bienestar',
      title1: 'Sin cirugía,',
      title2: 'con resultados',
      description: 'Procedimientos mínimamente invasivos que ofrecen resultados visibles sin los tiempos de recuperación de la cirugía. Técnicas avanzadas con tecnología de última generación.',
      moreInfo: 'Más información',
      ctaEyebrow: 'Consulta Personalizada',
      ctaTitle: '¿Qué tratamiento es adecuado para usted?',
      ctaDesc: 'En una consulta de valoración analizaremos sus necesidades y le propondremos el tratamiento más adecuado.',
      ctaButton: 'Solicitar valoración',
    },
    en: {
      eyebrow: 'Aesthetic Medicine & Wellness',
      title1: 'Without surgery,',
      title2: 'with results',
      description: 'Minimally invasive procedures that offer visible results without surgical recovery times. Advanced techniques with state-of-the-art technology.',
      moreInfo: 'More information',
      ctaEyebrow: 'Personalized Consultation',
      ctaTitle: 'Which treatment is right for you?',
      ctaDesc: 'In an assessment consultation we will analyze your needs and propose the most suitable treatment.',
      ctaButton: 'Request assessment',
    },
    fr: {
      eyebrow: 'Médecine Esthétique et Bien-être',
      title1: 'Sans chirurgie,',
      title2: 'avec résultats',
      description: 'Procédures peu invasives offrant des résultats visibles sans les temps de récupération de la chirurgie. Techniques avancées avec technologie de pointe.',
      moreInfo: 'Plus d\'informations',
      ctaEyebrow: 'Consultation Personnalisée',
      ctaTitle: 'Quel traitement vous convient?',
      ctaDesc: 'Lors d\'une consultation d\'évaluation, nous analyserons vos besoins et vous proposerons le traitement le plus adapté.',
      ctaButton: 'Demander une évaluation',
    },
    pt: {
      eyebrow: 'Medicina Estética e Bem-estar',
      title1: 'Sem cirurgia,',
      title2: 'com resultados',
      description: 'Procedimentos minimamente invasivos que oferecem resultados visíveis sem os tempos de recuperação da cirurgia. Técnicas avançadas com tecnologia de última geração.',
      moreInfo: 'Mais informações',
      ctaEyebrow: 'Consulta Personalizada',
      ctaTitle: 'Qual tratamento é adequado para você?',
      ctaDesc: 'Em uma consulta de avaliação analisaremos suas necessidades e proporemos o tratamento mais adequado.',
      ctaButton: 'Solicitar avaliação',
    },
  };

  const procedimientos = {
    es: [
      { titulo: 'Rinomodelación®', icono: CircleDot, descripcion: 'Corrección no quirúrgica de la nariz. Marca registrada OEPM. Resultados inmediatos.', url: '/rinomodelacion' },
      { titulo: 'Endolifting Láser', icono: Zap, descripcion: 'Rejuvenecimiento facial sin cicatrices. Tecnología láser de última generación.', url: '/endolifting' },
      { titulo: 'Eliminación de Papada', icono: ScanFace, descripcion: 'Reducción del tejido submentoniano. Perfil definido sin cirugía.', url: '/papada' },
      { titulo: 'Elevación de Cejas', icono: Sparkles, descripcion: 'Apertura de la mirada. Técnica no invasiva con resultados naturales.', url: '/elevacion-cejas' },
      { titulo: 'Armonización Facial', icono: Users, descripcion: 'Equilibrio y proporción facial. Fillers, estimuladores de colágeno y neuromoduladores.', url: '/armonizacion-facial' },
      { titulo: 'Ojeras sin Cirugía', icono: Eye, descripcion: 'Tratamiento de ojeras y surcos con técnicas no invasivas.', url: '/ojeras-sin-cirugia' },
      { titulo: 'Medicina Integrativa', icono: Leaf, descripcion: 'IV Drips, terapia hormonal y bienestar integral.', url: '/medicina-integrativa' },
    ],
    en: [
      { titulo: 'Rhinomodeling®', icono: CircleDot, descripcion: 'Non-surgical nose correction. OEPM registered trademark. Immediate results.', url: '/rinomodelacion' },
      { titulo: 'Laser Endolifting', icono: Zap, descripcion: 'Facial rejuvenation without scars. State-of-the-art laser technology.', url: '/endolifting' },
      { titulo: 'Jowl Removal', icono: ScanFace, descripcion: 'Submental tissue reduction. Defined profile without surgery.', url: '/papada' },
      { titulo: 'Brow Lift', icono: Sparkles, descripcion: 'Eye opening. Non-invasive technique with natural results.', url: '/elevacion-cejas' },
      { titulo: 'Facial Harmonization', icono: Users, descripcion: 'Facial balance and proportion. Fillers, collagen stimulators and neuromodulators.', url: '/armonizacion-facial' },
      { titulo: 'Non-Surgical Dark Circles', icono: Eye, descripcion: 'Treatment of dark circles and furrows with non-invasive techniques.', url: '/ojeras-sin-cirugia' },
      { titulo: 'Integrative Medicine', icono: Leaf, descripcion: 'IV Drips, hormonal therapy and integral wellness.', url: '/medicina-integrativa' },
    ],
    fr: [
      { titulo: 'Rhinomodelage®', icono: CircleDot, descripcion: 'Correction non chirurgicale du nez. Marque déposée OEPM. Résultats immédiats.', url: '/rinomodelacion' },
      { titulo: 'Endolifting Laser', icono: Zap, descripcion: 'Rajeunissement facial sans cicatrices. Technologie laser de pointe.', url: '/endolifting' },
      { titulo: 'Élimination du Double Menton', icono: ScanFace, descripcion: 'Réduction du tissu sous-mentonnier. Profil défini sans chirurgie.', url: '/papada' },
      { titulo: 'Lifting des Sourcils', icono: Sparkles, descripcion: 'Ouverture du regard. Technique non invasive aux résultats naturels.', url: '/elevacion-cejas' },
      { titulo: 'Harmonisation Faciale', icono: Users, descripcion: 'Équilibre et proportion faciale. Fillers, stimulateurs de collagène et neuromodulateurs.', url: '/armonizacion-facial' },
      { titulo: 'Cernes sans Chirurgie', icono: Eye, descripcion: 'Traitement des cernes et des sillons avec des techniques non invasives.', url: '/ojeras-sin-cirugia' },
      { titulo: 'Médecine Intégrative', icono: Leaf, descripcion: 'IV Drips, thérapie hormonale et bien-être intégral.', url: '/medicina-integrativa' },
    ],
    pt: [
      { titulo: 'Rinomodelação®', icono: CircleDot, descripcion: 'Correção não cirúrgica do nariz. Marca registrada OEPM. Resultados imediatos.', url: '/rinomodelacion' },
      { titulo: 'Endolifting Laser', icono: Zap, descripcion: 'Rejuvenescimento facial sem cicatrizes. Tecnologia laser de última geração.', url: '/endolifting' },
      { titulo: 'Eliminação de Papada', icono: ScanFace, descripcion: 'Redução do tecido submentoniano. Perfil definido sem cirurgia.', url: '/papada' },
      { titulo: 'Elevação de Sobrancelhas', icono: Sparkles, descripcion: 'Abertura do olhar. Técnica não invasiva com resultados naturais.', url: '/elevacion-cejas' },
      { titulo: 'Harmonização Facial', icono: Users, descripcion: 'Equilíbrio e proporção facial. Fillers, estimuladores de colágeno e neuromoduladores.', url: '/armonizacion-facial' },
      { titulo: 'Olheiras sem Cirurgia', icono: Eye, descripcion: 'Tratamento de olheiras e sulcos com técnicas não invasivas.', url: '/ojeras-sin-cirugia' },
      { titulo: 'Medicina Integrativa', icono: Leaf, descripcion: 'IV Drips, terapia hormonal e bem-estar integral.', url: '/medicina-integrativa' },
    ],
  };

  const t = texts[lang] || texts.es;
  const procs = procedimientos[lang] || procedimientos.es;

  return (
    <div className="min-h-screen bg-ivory">
      <Header />
      
      <section className="pt-12 pb-20 bg-cream">
        <div className="container mx-auto px-6 max-w-5xl text-center">
          <AnimatedSection animation="fadeUp">
            <p className="eyebrow mb-6">{t.eyebrow}</p>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-charcoal mb-6">
              {t.title1}<br /><span className="text-gold">{t.title2}</span>
            </h1>
            <div className="w-16 h-px bg-gold mx-auto mb-8"></div>
            <p className="text-text-secondary text-lg max-w-3xl mx-auto">{t.description}</p>
          </AnimatedSection>
        </div>
      </section>

      <section className="py-20 bg-ivory">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="grid gap-8">
            {procs.map((proc, index) => {
              const Icon = proc.icono;
              return (
                <AnimatedSection key={index} animation="fadeUp" delay={index * 0.1}>
                  <Link to={proc.url} className="group card-luxury p-8 flex flex-col md:flex-row gap-8 items-start">
                    <div className="w-16 h-16 border border-gold flex items-center justify-center flex-shrink-0 group-hover:bg-gold transition-colors">
                      <Icon className="text-gold group-hover:text-charcoal transition-colors" size={24} />
                    </div>
                    <div className="flex-1">
                      <h2 className="font-serif text-2xl text-charcoal mb-3 group-hover:text-gold transition-colors">{proc.titulo}</h2>
                      <p className="text-text-secondary leading-relaxed mb-4">{proc.descripcion}</p>
                      <span className="inline-flex items-center gap-2 text-gold text-sm tracking-wider uppercase group-hover:gap-3 transition-all">
                        {t.moreInfo} <ArrowRight size={14} />
                      </span>
                    </div>
                  </Link>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-charcoal">
        <div className="container mx-auto px-6 max-w-3xl text-center">
          <AnimatedSection animation="fadeUp">
            <p className="eyebrow text-gold mb-6">{t.ctaEyebrow}</p>
            <h2 className="font-serif text-3xl md:text-4xl text-ivory mb-6">{t.ctaTitle}</h2>
            <p className="text-gold-light/70 mb-10">{t.ctaDesc}</p>
            <Link to="/contacto" className="btn-gold inline-flex items-center gap-2 group">
              {t.ctaButton}
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </AnimatedSection>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default MedicinaEstetica;
